import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { BarcodeFormat, BarcodeScanner, LensFacing } from "@capacitor-mlkit/barcode-scanning";

export interface ContinuousQrScannerHandle {
  stop(): Promise<void>;
}

export interface ContinuousQrScannerOptions {
  onPayload(rawValue: string): void;
  onError?(message: string): void;
  videoElement?: HTMLVideoElement;
}

async function ensureWebBarcodeDetector(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return;
  }

  await import("barcode-detector/polyfill");
}

async function ensureCameraPermission(): Promise<void> {
  const current = await BarcodeScanner.checkPermissions();

  if (current.camera === "granted") {
    return;
  }

  const requested = await BarcodeScanner.requestPermissions();

  if (requested.camera !== "granted") {
    throw new Error("QR_CAMERA_PERMISSION_DENIED");
  }
}

export async function startContinuousQrScanner(
  options: ContinuousQrScannerOptions,
): Promise<ContinuousQrScannerHandle> {
  await ensureWebBarcodeDetector();

  const supported = await BarcodeScanner.isSupported();

  if (!supported.supported) {
    throw new Error("QR_SCANNER_UNSUPPORTED");
  }

  await ensureCameraPermission();

  if (!Capacitor.isNativePlatform() && !options.videoElement) {
    throw new Error("QR_WEB_VIDEO_ELEMENT_REQUIRED");
  }

  const nativeScanner = Capacitor.isNativePlatform();

  if (nativeScanner && typeof document !== "undefined") {
    document.documentElement.classList.add("barcode-scanner-active");
    document.body.classList.add("barcode-scanner-active");
  }

  const barcodeListener: PluginListenerHandle = await BarcodeScanner.addListener(
    "barcodesScanned",
    (event) => {
      for (const barcode of event.barcodes) {
        const value = barcode.rawValue?.trim() || barcode.displayValue?.trim();

        if (value) {
          options.onPayload(value);
        }
      }
    },
  );

  const errorListener: PluginListenerHandle = await BarcodeScanner.addListener(
    "scanError",
    (event) => {
      options.onError?.(event.message);
    },
  );

  try {
    await BarcodeScanner.startScan({
      formats: [BarcodeFormat.QrCode],
      lensFacing: LensFacing.Back,
      ...(options.videoElement ? { videoElement: options.videoElement } : {}),
    });
  } catch (error) {
    await barcodeListener.remove();
    await errorListener.remove();
    if (nativeScanner && typeof document !== "undefined") {
      document.documentElement.classList.remove("barcode-scanner-active");
      document.body.classList.remove("barcode-scanner-active");
    }
    throw error;
  }

  let stopped = false;

  return {
    async stop() {
      if (stopped) {
        return;
      }

      stopped = true;
      await barcodeListener.remove();
      await errorListener.remove();
      try {
        await BarcodeScanner.stopScan();
      } finally {
        if (nativeScanner && typeof document !== "undefined") {
          document.documentElement.classList.remove("barcode-scanner-active");
          document.body.classList.remove("barcode-scanner-active");
        }
      }
    },
  };
}
